import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Shield, Save, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { FeatureId, Permission } from '../../types/permissions';

interface FeatureGroup {
  id: FeatureId;
  name: string;
  description: string;
}

export default function RoleManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<string>('worker');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const features: FeatureGroup[] = [
    { id: 'order_management', name: 'Order Management', description: 'Manage track bookings and orders' },
    { id: 'user_management', name: 'User Management', description: 'Manage user accounts and profiles' },
    { id: 'worker_management', name: 'Worker Management', description: 'Manage worker accounts and permissions' },
    { id: 'event_management', name: 'Event Management', description: 'Manage racing events and categories' },
    { id: 'category_management', name: 'Category Management', description: 'Manage racing categories' },
    { id: 'event_registrations', name: 'Event Registrations', description: 'Manage event registrations' },
    { id: 'track_management', name: 'Track Management', description: 'Manage track settings and pricing' },
    { id: 'reports_analytics', name: 'Reports & Analytics', description: 'View and generate reports' },
    { id: 'calendar_view', name: 'Calendar View', description: 'View and manage track schedule' },
    { id: 'email_templates', name: 'Email Templates', description: 'Manage email templates' },
    { id: 'system_settings', name: 'System Settings', description: 'Configure system-wide settings' },
    { id: 'customer_management', name: 'Customer Management', description: 'Manage customer accounts and data' },
  ];

  const roles = [
    { id: 'worker', name: 'Worker' },
    { id: 'user', name: 'User' },
    { id: 'driver', name: 'Driver' },
    { id: 'promotor', name: 'Promotor' },
  ];

  useEffect(() => {
    fetchPermissions();
  }, [user, selectedRole]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*')
        .eq('role', selectedRole);

      if (error) throw error;
      setPermissions(data || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (featureId: FeatureId) => {
    setPermissions(prev => {
      const newPermissions = [...prev];
      const index = newPermissions.findIndex(p => p.feature_id === featureId);
      
      if (index >= 0) {
        // Update existing permission
        newPermissions[index] = {
          ...newPermissions[index],
          can_access: !newPermissions[index].can_access
        };
      } else {
        // Create new permission
        newPermissions.push({
          id: crypto.randomUUID(),
          role: selectedRole,
          feature_id: featureId,
          can_access: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
      
      setHasChanges(true);
      return newPermissions;
    });
  };

  const savePermissions = async () => {
    try {
      setSaving(true);
      
      // Prepare upsert data
      const upsertData = permissions.map(p => ({
        role: p.role,
        feature_id: p.feature_id,
        can_access: p.can_access
      }));
      
      // Upsert permissions
      const { error } = await supabase
        .from('role_permissions')
        .upsert(upsertData, { onConflict: 'role,feature_id' });

      if (error) throw error;
      
      toast.success('Permissions saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionValue = (featureId: FeatureId): boolean => {
    const permission = permissions.find(p => p.feature_id === featureId);
    return permission ? permission.can_access : false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Role Permissions</h1>
          
          <div className="flex items-center gap-4">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="bg-zinc-900 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            
            <button
              onClick={savePermissions}
              disabled={!hasChanges || saving}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="h-5 w-5" />
              Save Permissions
            </button>
          </div>
        </div>

        {hasChanges && (
          <div className="mb-6 p-4 bg-amber-900 border border-amber-700 rounded-lg flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-300 flex-shrink-0" />
            <p className="text-amber-200 text-sm">
              You have unsaved changes. Click "Save Permissions" to apply them.
            </p>
          </div>
        )}

        <div className="bg-zinc-900 rounded-lg p-6 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-6 w-6 text-primary-500" />
            <h2 className="text-xl font-semibold text-white">
              Configure what features the <span className="text-primary-500">{selectedRole}</span> role can access. Changes will apply to all users with this role.
            </h2>
          </div>

          <div className="space-y-6">
            {features.map((feature) => (
              <div key={feature.id} className="bg-zinc-800 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{feature.name}</h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={getPermissionValue(feature.id)}
                      onChange={() => togglePermission(feature.id)}
                    />
                    <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}